import {Helmet} from 'react-helmet-async';

type OrganizationSchema = {
  type: 'Organization';
  name: string;
  url: string;
  logo?: string;
  description?: string;
  sameAs?: string[];
};

type WebApplicationSchema = {
  type: 'WebApplication';
  name: string;
  description: string;
  url: string;
  applicationCategory?: string;
  operatingSystem?: string;
  offers?: {
    price: string;
    priceCurrency: string;
  };
};

type ArticleSchema = {
  type: 'Article';
  headline: string;
  description?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  url: string;
  /** BCP-47 language of the article body (help articles are per-locale). */
  inLanguage?: string;
};

type BreadcrumbItem = {
  name: string;
  url: string;
};

type BreadcrumbListSchema = {
  type: 'BreadcrumbList';
  items: BreadcrumbItem[];
};

type FAQItem = {
  question: string;
  answer: string;
};

type FAQPageSchema = {
  type: 'FAQPage';
  questions: FAQItem[];
};

type SchemaData =
  | OrganizationSchema
  | WebApplicationSchema
  | ArticleSchema
  | BreadcrumbListSchema
  | FAQPageSchema;

type StructuredDataProps = {
  data: SchemaData | SchemaData[];
};

const buildSchema = (data: SchemaData): object => {
  switch (data.type) {
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: data.name,
        url: data.url,
        logo: data.logo,
        description: data.description,
        sameAs: data.sameAs,
      };

    case 'WebApplication':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: data.name,
        description: data.description,
        url: data.url,
        applicationCategory: data.applicationCategory || 'WeatherApplication',
        operatingSystem: data.operatingSystem || 'All',
        offers: data.offers
          ? {
              '@type': 'Offer',
              price: data.offers.price,
              priceCurrency: data.offers.priceCurrency,
            }
          : undefined,
      };

    case 'Article':
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: data.headline,
        description: data.description,
        datePublished: data.datePublished,
        dateModified: data.dateModified,
        author: data.author
          ? {
              '@type': 'Organization',
              name: data.author,
            }
          : undefined,
        url: data.url,
        inLanguage: data.inLanguage,
        publisher: {
          '@type': 'Organization',
          name: 'SmartMixin',
          url: 'https://smartmixin.io',
        },
      };

    case 'BreadcrumbList':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: data.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      };

    case 'FAQPage':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: data.questions.map((q) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      };

    default:
      return {};
  }
};

const StructuredData = ({data}: StructuredDataProps) => {
  const schemas = Array.isArray(data) ? data : [data];

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(buildSchema(schema))}
        </script>
      ))}
    </Helmet>
  );
};

export default StructuredData;
export type {
  OrganizationSchema,
  WebApplicationSchema,
  ArticleSchema,
  BreadcrumbListSchema,
  FAQPageSchema,
  BreadcrumbItem,
  FAQItem,
};
